"use client";

import Image from "next/image";
import {type CSSProperties, type FormEvent, useId, useRef, useState} from "react";
import {motion, type MotionValue, useReducedMotion, useScroll, useSpring, useTransform,} from "framer-motion";
import type SwiperCore from "swiper";
import {Swiper, SwiperSlide} from "swiper/react";
import "swiper/css";
import "./ReputechLandingPage.css";
import {IconReviewStarGenerator} from "@/components/Icons/IconReviewStarGenerator";
import {useRouter} from "next/navigation";
import {LandingThemeToggle} from "./LandingThemeToggle";
import AlertTriangleIcon from "@/images/landing-redesign/rating-icons/alert-triangle.svg";
import ArrowRightIcon from "@/images/landing-redesign/rating-icons/arrow-right.svg";
import Below4Rating from "@/images/landing-redesign/bad-review/below-4-review.png";
import ImpactReviews from "@/images/landing-redesign/impact-reviews-3x.png";
import ImpactChart from "@/images/landing-redesign/impact-chart-3x.png";
import ImpactGridHorizontal from "@/images/landing-redesign/impact-grid-horizontal.svg";
import ImpactGridVertical from "@/images/landing-redesign/impact-grid-vertical.svg";
import BadReviewTrustpilot from "@/images/landing-redesign/bad-review/bad-review-trustpilot.png";
import BenefitDarkIcon from "@/images/landing-redesign/hero/benefit-dark.svg";
import BenefitLightIcon from "@/images/landing-redesign/hero/benefit-light.svg";
import CheckIcon from "@/images/landing-redesign/figma-qa/check.svg";
import CoinsIcon from "@/images/landing-redesign/rating-icons/coins.svg";
import FaqExpandedIcon from "@/images/landing-redesign/figma-qa/faq-expanded.svg";
import FaqCollapsedIcon from "@/images/landing-redesign/figma-qa/faq-collapsed.svg";
import CtaLeftPerson from "@/images/landing-redesign/cta/left-person.png";
import CtaGridHorizontal from "@/images/landing-redesign/cta/grid-horizontal.svg";
import CtaGridVertical from "@/images/landing-redesign/cta/grid-vertical.svg";
import CtaRightPerson from "@/images/landing-redesign/cta/right-person.png";
import FeaturedSparkleIcon from "@/images/landing-redesign/figma-qa/featured-sparkle.svg";
import FooterImage from "@/images/landing-redesign/footer-advisor.png";
import FooterSubmitArrow from "@/images/landing-redesign/figma-qa/footer-submit-arrow.svg";
import GuaranteeIcon from "@/images/landing-redesign/hero/guarantee.png";
import HeroBusinessman from "@/images/landing-redesign/hero/hero-businessman-updated.png";
import HeroTradesman from "@/images/landing-redesign/hero/hero-tradesman.webp";
import HeroDoctor from "@/images/landing-redesign/hero/hero-doctor-updated.png";
import {HeroRatingCard} from "./HeroRatingCard";
import HeroBusinesswoman from "@/images/landing-redesign/hero/hero-businesswoman.png";
import ReviewerLiana from "@/images/landing-redesign/hero/reviewer-liana.jpg";
import ReviewStar from "@/images/landing-redesign/hero/review-star.svg";
import ReviewTrash from "@/images/landing-redesign/hero/review-trash.svg";
import HeroBackground from "@/images/landing-redesign/hero/hero-background-figma.png";
import HeroConnector from "@/images/landing-redesign/hero/hero-connector.svg";
import Visa from "@/images/landing-redesign/hero/visa.svg";
import Mastercard from "@/images/landing-redesign/hero/mastercard.svg";
import MapPinIcon from "@/images/landing-redesign/rating-icons/map-pin.svg";
import ProcessArrowIcon from "@/images/landing-redesign/process-arrow.svg";
import ProcessGridHorizontal from "@/images/landing-redesign/process-grid-horizontal.svg";
import ProcessGridVertical from "@/images/landing-redesign/process-grid-vertical.svg";
import ProcessStepMarker from "@/images/landing-redesign/process-step-marker.svg";
import ProcessCheckIcon from "@/images/landing-redesign/process-check.svg";
import ProcessSearchIcon from "@/images/landing-redesign/process-search.svg";
import ProcessTrackingActive from "@/images/landing-redesign/process-tracking-active.svg";
import ProcessTrackingCheck from "@/images/landing-redesign/process-tracking-check.svg";
import ProcessTrackingDeleted from "@/images/landing-redesign/process-tracking-deleted.svg";
import ProcessTrackingInactive from "@/images/landing-redesign/process-tracking-inactive.svg";
import ProcessTrackingLive from "@/images/landing-redesign/process-tracking-live.svg";
import PricingFeaturedBackground from "@/images/landing-redesign/pricing/featured.png";
import PricingCheckIcon from "@/images/landing-redesign/pricing/check.svg";
import PricingPanelBackground from "@/images/landing-redesign/pricing/pricing-panel.png";
import RatingConnectorImage from "@/images/landing-redesign/figma-qa/rating-connector.svg";
import ReadReviews from "@/images/landing-redesign/bad-review/read reviews.png";
import RiskCalculatorBackground from "@/images/landing-redesign/risk-calculator.png";
import RiskShield from "@/images/landing-redesign/figma-qa/risk-shield.png";
import ShieldAlertIcon from "@/images/landing-redesign/rating-icons/shield-alert.svg";
import SparklesIcon from "@/images/landing-redesign/rating-icons/sparkles.svg";
import TrendingDownIcon from "@/images/landing-redesign/rating-icons/trending-down.svg";
import TrendingUpIcon from "@/images/landing-redesign/rating-icons/trending-up.svg";
import UserCheckIcon from "@/images/landing-redesign/rating-icons/user-check.svg";
import {useSelector} from "react-redux";
import {selectPriceTierTrustPilot} from "@/redux/slice/ReviewTrustPilotSlice";
import classNames from "classnames";
import type {PriceTierItem, PriceTierType} from "@/types/trustpilotPricing";
import {isReputechTrustpilotPriceTier} from "@/utils/priceTierHelper";

const fallbackPricingTiers: PriceTierItem[] = [
  {min_reviews: 1, max_reviews: 9, price: 150},
  {min_reviews: 10, max_reviews: 49, price: 130},
  {min_reviews: 50, max_reviews: null, price: 100},
];

const getMarketingPricingTiers = (priceTier?: PriceTierType) => {
  const tiers = isReputechTrustpilotPriceTier(priceTier)
    ? priceTier.tiers
    : fallbackPricingTiers;

  return tiers.map((tier) => ({
    min_reviews: tier.min_reviews,
    max_reviews: tier.max_reviews,
    range: tier.max_reviews === null
      ? `${tier.min_reviews}+ reviews`
      : `${tier.min_reviews}–${tier.max_reviews} reviews`,
    price: new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(tier.price),
  }));
};

const getProcessSteps = () => [
  {
    roman: "I",
    title: "Find your business",
    copy: "Enter a business name or Trustpilot profile link. We will find the profile and its reviews.",
    note: "Search by business name or profile URL.",
  },
  {
    roman: "II",
    title: "Choose your profile",
    copy: "Select the matching Trustpilot profile to see its rating and reviews.",
    note: "Review one profile per order.",
  },
  {
    roman: "III",
    title: "Select reviews",
    copy: "Choose the reviews you want us to assess. We prepare and submit a report to Trustpilot.",
    note: "You only pay for reviews Trustpilot removes.",
  },
  {
    roman: "IV",
    title: "Follow the outcome",
    copy: "We keep you informed while Trustpilot reviews the report.",
    note: "",
  },
];

const faqCategories = [
  {
    label: "General",
    items: [
      ["What does Reputech do?", "We help businesses report Trustpilot reviews that may violate the platform's rules. You choose the reviews and share the details, then we prepare and submit the reports."],
      ["Why report an unfair review?", "A review that breaks Trustpilot's rules can affect customer confidence. We help you raise a clear, evidence-based report without challenging legitimate feedback."],
      ["Can you guarantee that a review will be removed?", "No. Trustpilot decides whether a review violates its rules. We assess the details and submit a report, but cannot promise an outcome."],
      ["Which reviews can be reported?", "We assess reviews that may violate Trustpilot's rules, such as content that is irrelevant, misleading, abusive, or not based on a genuine experience."],
    ],
  },
  {
    label: "Process",
    items: [
      [
        "How does the review reporting process work?",
        "Find your Trustpilot profile, select the reviews you want assessed, and share the requested details. We prepare and submit the reports to Trustpilot.",
      ],
      [
        "What happens after I select reviews?",
        "We review the information you provide and submit a report to Trustpilot. The platform reviews each report and makes the final decision.",
      ],
      [
        "What if Trustpilot does not remove a review?",
        "You are not charged for that review. We charge only for reviews Trustpilot removes.",
      ],
      [
        "How will I hear about the outcome?",
        "We will keep you informed as your case progresses and Trustpilot reviews the reports.",
      ],
    ],
  },
  {
    label: "Pricing & payment",
    items: [
      [
        "How much does the service cost?",
        "The per-review rate depends on the number of reviews in one order for one Trustpilot profile: €150 for 1–9, €130 for 10–49, and €100 for 50 or more reviews.",
      ],
      [
        "Do I pay upfront?",
        "No upfront payment is taken. A card is required before we submit your request, but we charge only for reviews Trustpilot removes.",
      ],
      [
        "How is the total calculated?",
        "The rate is based on the total number of reviews in a single order for one profile. That rate applies to every review in that order, and you see the total before submitting.",
      ],
    ],
  },
  {
    label: "Privacy & support",
    items: [
      [
        "Is reporting a review allowed?",
        "You may report a review through Trustpilot's process if you believe it breaks the platform's rules. We do not report legitimate criticism simply because it is negative.",
      ],
      [
        "How is my information handled?",
        "We use the information you provide to assess and process your request, in line with our Privacy Policy and applicable data protection requirements.",
      ],
      [
        "How can I contact Reputech?",
        "Email us at support@reputech.io with questions about your request or the service.",
      ],
      [
        "Can I ask a question before starting?",
        "Yes. Email support@reputech.io and our team can help clarify the process before you submit a request.",
      ],
    ],
  },
];

const sectionClass =
  "rh2-section relative px-[max(24px,calc((100vw-1280px)/2))] py-20 max-[800px]:px-5 max-[800px]:py-16";
const kickerClass =
  "rh2-kicker font-jetbrains-mono text-[11px] font-medium uppercase leading-[1.5] tracking-[3.08px] text-[var(--brand)]";
const headingClass =
  "rh2-heading mx-auto mb-10 max-w-[914px] text-center max-[800px]:mb-8 [&>h2]:mt-4 [&>h2]:text-balance [&>h2]:text-[clamp(38px,3.35vw,48px)] [&>h2]:font-semibold [&>h2]:leading-[48px] [&>h2]:tracking-normal max-[800px]:[&>h2]:text-[clamp(34px,9vw,44px)] max-[800px]:[&>h2]:leading-[1.05] [&>h2_span]:text-[var(--brand)] [&>p:last-child]:mx-auto [&>p:last-child]:mt-4 [&>p:last-child]:max-w-[712px] [&>p:last-child]:text-lg [&>p:last-child]:leading-[26px] [&>p:last-child]:text-[var(--muted)] max-[800px]:[&>p:last-child]:text-base";
const lightHeadingClass = `${headingClass} is-light text-white [&_.rh2-kicker]:text-[var(--mint)] [&>p:last-child]:text-white/60`;
const pillClass =
  "rh2-pill inline-flex min-h-11 cursor-pointer items-center justify-center gap-3.5 rounded-full border-0 bg-[var(--brand)] py-[7px] pl-[18px] pr-2 text-[15px] font-[650] !text-white";

const Check = () => (
  <span
    className="rh2-check inline-grid size-5 shrink-0 place-items-center rounded-full bg-[var(--brand)] text-[11px] font-extrabold text-white [&>img]:size-full"
    aria-hidden="true"
  >
    <Image src={CheckIcon} alt="" width={20} height={20} />
  </span>
);
const RatingConnector = () => (
  <Image
    className="rh2-rating-connector h-2 w-8 flex-none object-fill max-[800px]:hidden"
    src={RatingConnectorImage}
    alt=""
    width={38}
    height={8}
  />
);
const Arrow = () => (
  <span
    className="rh2-arrow inline-grid size-[30px] place-items-center rounded-full bg-white text-base text-[var(--brand)]"
    aria-hidden="true"
  >
    ↗
  </span>
);
const HeroBenefitCheck = () => (
  <span
    className="rh2-hero-benefit-check inline-grid size-5 shrink-0 place-items-center rounded-full bg-[#3c6e711a] [&>img]:size-3"
    aria-hidden="true"
  >
    <Image
      className="is-light"
      src={BenefitLightIcon}
      alt=""
      width={12}
      height={12}
    />
    <Image
      className="is-dark"
      src={BenefitDarkIcon}
      alt=""
      width={12}
      height={12}
    />
  </span>
);

function ReputechSearchForm({submitLabel = "Find your business"}: {submitLabel?: string}) {
  const router = useRouter();
  const inputId = useId();
  const [query, setQuery] = useState("");

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const business = query.trim();
    if (business) router.push(`/search?q=${encodeURIComponent(business)}`);
  };

  return (
    <form className="rh-inline-search-form" onSubmit={submitSearch} role="search">
      <div>
        <div>
          <div>
            <label className="sr-only" htmlFor={inputId}>
              Business name or Trustpilot profile URL
            </label>
            <input
              id={inputId}
              autoComplete="organization"
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Business name or Trustpilot profile URL"
              value={query}
            />
          </div>
        </div>
      </div>
      <button disabled={!query.trim()} type="submit" aria-describedby={`${inputId}-hint`}>
        <span>{submitLabel}</span>
      </button>
      <span id={`${inputId}-hint`} className="sr-only">
        {query.trim() ? "Ready to search." : "Enter a business name or Trustpilot profile URL to search."}
      </span>
    </form>
  );
}

function CardBrandLogos() {
  return (
    <div className="rh2-payment-logos" aria-label="Accepted card brands: Visa, Mastercard, and American Express">
      <b><Image src={Visa} alt="Visa" width={32} height={32} /></b>
      <b><Image src={Mastercard} alt="Mastercard" width={25} height={20} /></b>
      <b className="is-amex">AMEX</b>
    </div>
  );
}

const processBrowserTitles = [
  "Find your business",
  "Choose your profile",
  "Select reviews",
  "Track your case",
];

const processLocations = [
  ["Harbor & Hearth, Downtown", "201 Congress Avenue, Austin, TX"],
  [
    "Harbor & Hearth, South Congress",
    "1600 South Congress Avenue, Austin, TX",
  ],
  [
    "Harbor & Hearth, East Austin",
    "901 East 5th Street, Austin, TX",
  ],
];

const processReviews = [
  ["1★", '“Completely unacceptable. Never again.”'],
  ["1★", '“Stay away from this business.”'],
  ["2★", '“Long wait and unfriendly service.”'],
];

function ProcessBrowserHeader() {
  return (
    <div className="flex h-[46px] items-center border-b border-black/[.06] bg-[#edf1f199] px-4">
      <div className="flex gap-1.5" aria-hidden="true">
        <i className="size-2.5 rounded-full bg-[#ff5f57]" />
        <i className="size-2.5 rounded-full bg-[#febc2e]" />
        <i className="size-2.5 rounded-full bg-[#28c840]" />
      </div>
      <span className="ml-5 flex h-[25px] w-[157px] items-center rounded-md px-3 text-[11px] leading-[16.5px] text-[#8a8a98] drop-shadow-[0_2px_5px_rgba(0,0,0,.05)] max-[480px]:w-[145px]">
        https://reputech.io/
      </span>
    </div>
  );
}
function ProcessActionArrow({size = 32}: {size?: number}) {
  return (
    <span
      className="grid flex-none place-items-center rounded-full bg-white"
      style={{width: size, height: size}}
    >
      <Image src={ProcessArrowIcon} alt="" width={14} height={14} />
    </span>
  );
}

function ProcessStepOne() {
  return (
    <div className="mt-4 flex h-[185px] flex-col rounded-[20px] border border-[#3c6e7138] bg-[linear-gradient(163deg,rgba(60,110,113,.14),rgba(60,110,113,.05)_55%,rgba(255,255,255,.9))] px-4 pt-6">
      <div className="flex h-[49px] items-center gap-3 rounded-full bg-white px-4 text-sm text-black/90 shadow-[0_2px_5px_rgba(0,0,0,.05)]">
        <Image src={ProcessSearchIcon} alt="" width={18} height={18} />
        <span className="truncate">Harbor &amp; Hearth, Austin</span>
      </div>
      <div className="mt-3 flex h-[46px] items-center justify-between rounded-full bg-[#3c6e71] pl-5 pr-[7px] text-sm font-semibold text-white">
        <span>Select reviews</span>
        <ProcessActionArrow />
      </div>
      <p className="mt-4 w-full text-center text-xs leading-[18px] text-[#666572]">
        Card required before submission
      </p>
    </div>
  );
}

function ProcessStepTwo() {
  return (
    <div className="mt-4 flex flex-col gap-2.5">
      {processLocations.map(([name, address], index) => (
        <div
          className={`flex h-[62px] items-center justify-between gap-3 rounded-xl border px-4 ${index === 0 ? "border-[#3c6e71] bg-[#3c6e710a]" : "border-black/[.06] bg-white"}`}
          key={name}
        >
          <span className="min-w-0">
            <strong className="block truncate text-[13px] font-semibold leading-[19.5px] tracking-[-.2px] text-black/90">
              {name}
            </strong>
            <small className="block truncate text-[11px] leading-[16.5px] text-[#8a8a98]">
              {address}
            </small>
          </span>
          <span
            className={`flex h-[34px] flex-none items-center rounded-full px-4 text-xs font-semibold ${index === 0 ? "bg-[#3c6e71] text-white" : "bg-[#edf1f1] text-[#666572]"}`}
          >
            {index === 0 ? "Selected ✓" : "Select"}
          </span>
        </div>
      ))}
    </div>
  );
}

function ProcessStepThree() {
  return (
    <div className="mt-4">
      <div className="flex flex-col gap-2.5">
        {processReviews.map(([rating, review]) => (
          <div
            className="flex h-[46px] min-w-0 items-center rounded-xl border border-[#3c6e71] bg-[#3c6e710a] px-4"
            key={review}
          >
            <span className="grid size-5 flex-none place-items-center rounded-md bg-[#3c6e71]">
              <Image src={ProcessCheckIcon} alt="" width={12} height={12} />
            </span>
            <strong className="ml-3 flex-none text-[11px] font-bold leading-[16.5px] text-[#c9503f]">
              {rating}
            </strong>
            <span className="ml-3 truncate text-[12.5px] leading-[18.75px] tracking-[-.35px] text-[#666572]">
              {review}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-4 flex h-[46px] items-center justify-between rounded-full bg-[#3c6e71] pl-5 pr-[9px] text-[13.5px] font-semibold text-white">
        <span>Submit 3 review reports</span>
        <ProcessActionArrow size={28} />
      </div>
    </div>
  );
}

function ProcessStepFour() {
  const stages = ["Received", "Prepared", "Submitted", "Reviewed"];

  return (
    <div className="mt-9 min-w-0">
      <div className="flex items-center justify-end gap-1.5 text-base font-semibold leading-[19px] tracking-[-.4px] text-[#00ad5c]">
        <Image src={ProcessTrackingLive} alt="" width={8} height={8} />
        LIVE
      </div>
      <div className="relative mt-4 h-[124px] min-w-0 rounded-xl border border-[#3c6e711a] bg-[#3c6e7103] px-3 py-4 max-[380px]:px-2">
        <Image
          className="absolute left-[2.54%] top-[52px] h-[3px] w-[57.62%]"
          src={ProcessTrackingActive}
          alt=""
          width={318}
          height={3}
        />
        <Image
          className="absolute left-[56.88%] top-[52px] h-[3px] w-[39.32%] max-w-full"
          src={ProcessTrackingInactive}
          alt=""
          width={207}
          height={3}
        />
        <div className="relative z-10 grid h-full min-w-0 translate-y-[18px] grid-cols-[repeat(4,minmax(0,1fr))] items-start">
          {stages.map((stage, index) => (
            <div
              className="flex min-w-0 flex-col items-center gap-2.5"
              key={stage}
            >
              {index < 2 && (
                <span className="grid size-[38px] place-items-center rounded-full bg-[#3c6e71]">
                  <Image
                    src={ProcessTrackingCheck}
                    alt=""
                    width={19}
                    height={19}
                  />
                </span>
              )}
              {index === 2 && (
                <span className="-mt-[7.145px] grid size-[52.392px] place-items-center rounded-full border-[6.351px] border-[#3c6e711a]">
                  <span className="grid size-[38.103px] place-items-center rounded-full bg-[#3c6e71]">
                    <Image
                      src={ProcessTrackingCheck}
                      alt=""
                      width={19.052}
                      height={19.052}
                    />
                  </span>
                </span>
              )}
              {index === 3 && (
                <Image
                  src={ProcessTrackingDeleted}
                  alt=""
                  width={38}
                  height={38}
                />
              )}
              <strong className="max-w-full text-center text-[12.7px] font-semibold leading-[15px] tracking-[-.3px] text-black/90 max-[380px]:text-[10px] max-[380px]:leading-3 max-[380px]:tracking-[-.4px]">
                {stage}
              </strong>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BrowserDemo({step}: {step: number}) {
  const bodies = [
    <ProcessStepOne key="one" />,
    <ProcessStepTwo key="two" />,
    <ProcessStepThree key="three" />,
    <ProcessStepFour key="four" />,
  ];
  const heights = [312, 333, 353, 352];

  return (
    <div
      className="process-browser w-full min-w-0 overflow-hidden rounded-2xl border border-black/[.05] bg-white shadow-[0_30px_70px_-30px_rgba(0,0,0,.6)]"
      style={{height: heights[step - 1]}}
      aria-label={`Trustpilot process step ${step}`}
    >
      <ProcessBrowserHeader />
      <div className="px-6 pb-5 pt-6 max-[480px]:px-4">
        <p className="font-jetbrains-mono text-[10px] font-bold uppercase leading-[15px] tracking-[1.4px] text-[#8a8a98]">
          Step {step} · {processBrowserTitles[step - 1]}
        </p>
        {bodies[step - 1]}
      </div>
    </div>
  );
}

function TimelineProgressRail({progress}: {progress: MotionValue<number>}) {
  const shouldReduceMotion = useReducedMotion();
  const smoothProgress = useSpring(progress, {
    stiffness: 130,
    damping: 24,
    mass: 0.35,
  });
  const displayProgress = shouldReduceMotion ? progress : smoothProgress;
  const thumbTop = useTransform(displayProgress, [0, 1], ["0%", "100%"]);
  const thumbOffset = useTransform(displayProgress, [0, 1], ["0%", "-100%"]);

  return (
    <div
      className="process-progress pointer-events-none absolute inset-y-0 left-0 hidden w-[3px] md:block"
      aria-hidden="true"
    >
      <div className="absolute inset-y-0 left-0 w-[2px] bg-white/10">
        <motion.div
          className="absolute left-0 h-full max-h-[129.62px] w-[3px] rounded-full bg-white"
          style={{top: thumbTop, y: thumbOffset}}
        />
      </div>
    </div>
  );
}

function RatingProfileCard({after = false}: {after?: boolean}) {
  const widths = after
    ? [62.2, 2.85, 1.63, 0, 0]
    : [21.95, 7.32, 10.98, 8.94, 16.26];

  return (
    <div
      className={`rh2-rating-profile relative flex h-[317px] w-[310px] max-w-full flex-col gap-4 rounded-[20px] border p-[23px] shadow-[0_16px_16px_rgba(0,0,0,.05)] max-[680px]:w-full ${after ? "is-after border-[#00ad5c] bg-[#ebf8f2] shadow-[inset_0_4px_16px_rgba(62,180,137,.08),0_16px_16px_rgba(0,0,0,.05)] max-[1279px]:order-2 max-[680px]:order-4 [&_.rh2-rating-profile-top>small]:bg-[#00ad5c]" : "is-before border-[#f14336] bg-[#fffbfb] max-[1279px]:order-1"}`}
    >
      <div className="rh2-rating-profile-top flex items-center justify-between [&>i]:grid [&>i]:size-11 [&>i]:place-items-center [&>i]:rounded-xl [&>i]:border [&>i]:border-slate-800/[.06] [&>i]:bg-white [&>i]:text-xl [&>i]:not-italic [&>i]:font-bold [&>i]:shadow-[0_2px_3px_rgba(0,0,0,.02)] [&>small]:flex [&>small]:min-h-[25px] [&>small]:leading-[13px] [&>small]:items-center [&>small]:rounded-lg [&>small]:bg-[#f14336] [&>small]:px-3 [&>small]:py-1.5 [&>small]:text-[11px] [&>small]:font-bold [&>small]:text-white">
        <small>{after ? "POTENTIAL AFTER" : "CURRENT"}</small>
        <i>T</i>
      </div>
      <div className="rh2-rating-profile-name flex flex-col gap-1 [&>b]:text-base [&>b]:leading-[19px] [&>span]:leading-[14px] [&>span]:text-xs [&>span]:text-slate-500">
        <b>Harbor &amp; Hearth</b>
        <span>Sample Trustpilot profile</span>
      </div>
      <div className="rh2-rating-profile-score flex items-center gap-3 [&>span]:flex [&>span]:flex-col [&>span]:gap-1 [&>span>i]:text-sm [&>span>i]:not-italic [&>span>i]:tracking-[1px] [&>span>i]:text-[#ffac11] [&>span>i]:gap-0.5 [&>span>small]:leading-[13px] [&>span>small]:text-[11px] [&>span>small]:text-slate-500">
        <strong
          className={`text-[40px] font-black leading-[48px] ${after ? "text-[#00ad5c]" : "text-[#f14336]"}`}
        >
          {after ? "4.9" : "3.9"}
        </strong>
        <span>
          <i>
            <IconReviewStarGenerator size={14} count={after ? 5 : 4} />
          </i>
          <small>{after ? "138" : "158"} reviews</small>
        </span>
      </div>
      <div className="rh2-rating-bars flex flex-col gap-2">
        {widths.map((width, index) => (
          <div className="flex items-center gap-2" key={5 - index}>
            <span className="w-2 text-[10px] leading-3">{5 - index}</span>
            <i
              className={`block h-1.5 flex-auto overflow-hidden rounded-[3px] ${after ? "bg-white" : "bg-[#f4ecec]"}`}
            >
              <b
                className="block h-full rounded-[inherit] bg-[#ffac11]"
                style={{width: `${width}%`}}
              />
            </i>
          </div>
        ))}
      </div>
    </div>
  );
}

function RatingComparison() {
  const problems = [
    [
      "Lost customers",
      "Potential customers may choose another business.",
      TrendingDownIcon,
    ],
    [
      "Trust gap",
      "An unfair review can affect a first impression.",
      AlertTriangleIcon,
    ],
    [
      "Lower confidence",
      "A low rating can make customers hesitate.",
      ShieldAlertIcon,
    ],
    ["Wasted spend", "Ad clicks may not turn into enquiries.", CoinsIcon],
  ] as const;
  const benefits = [
    ["Customer confidence", "Clear information helps customers decide.", UserCheckIcon],
    ["More enquiries", "A fair profile can support customer decisions.", TrendingUpIcon],
    ["Stronger profile", "Your profile can better reflect your business.", MapPinIcon],
    [
      "A fair picture",
      "Address reviews that may break platform rules.",
      SparklesIcon,
    ],
  ] as const;

  return (
    <div
      className="rh2-rating-comparison relative mx-auto mt-10 grid min-h-[344px] w-full max-w-[1216px] grid-cols-[280px_310px_310px_280px] items-start gap-3 max-[1279px]:max-w-[632px] max-[1279px]:grid-cols-2 max-[680px]:max-w-full max-[680px]:grid-cols-1 max-[680px]:gap-3.5"
      role="group"
      aria-label="Illustration of a possible rating change after review removal"
    >
      <div className="rh2-rating-indicators is-problem flex h-[344px] flex-col justify-between max-[1279px]:order-3 max-[1279px]:w-[310px] max-[680px]:order-2 max-[680px]:w-full">
        {problems.map(([title, copy, icon]) => (
          <div
            className="rh2-rating-indicator relative flex min-h-[77px] w-[280px] items-center gap-2 bg-transparent p-0 max-[1279px]:w-full max-[680px]:!text-left"
            key={title}
          >
            <div className="rh2-rating-label flex min-h-[77px] w-60 items-center gap-3 rounded-xl bg-white p-3 shadow-[0_4px_6px_rgba(0,0,0,.03)] max-[1279px]:w-full max-[680px]:flex-row-reverse max-[680px]:[&>span]:items-start [&>i]:grid [&>i]:size-8 [&>i]:flex-none [&>i]:place-items-center [&>i]:rounded-lg [&>i]:bg-[rgba(229,75,75,.08)] [&>span]:flex [&>span]:min-w-0 [&>span]:flex-auto [&>span]:flex-col [&>span]:gap-0.5 [&_b]:whitespace-nowrap [&_b]:text-sm [&_b]:leading-[17px] [&_b]:text-black/90 [&_small]:text-xs [&_small]:leading-[1.4] [&_small]:text-[#666572]">
              <span>
                <b>{title}</b>
                <small>{copy}</small>
              </span>
              <i>
                <Image src={icon} alt="" width={16} height={16} />
              </i>
            </div>
            <span className="contents max-[1279px]:hidden">
              <RatingConnector />
            </span>
          </div>
        ))}
      </div>
      <RatingProfileCard />
      <span className="rh2-rating-swap absolute left-1/2 top-[100px] z-[3] grid size-11 -translate-x-1/2 place-items-center rounded-full bg-black/90 shadow-[0_11px_12px_rgba(0,182,122,.12)] backdrop-blur-[2px] max-[680px]:static max-[680px]:order-3 max-[680px]:mx-auto max-[680px]:my-[-3px] max-[680px]:translate-x-0 max-[680px]:rotate-90 [&>img]:size-4">
        <Image src={ArrowRightIcon} alt="" width={16} height={16} />
      </span>
      <RatingProfileCard after />
      <div className="rh2-rating-indicators is-benefit flex flex-col gap-3 [&>.rh2-rating-indicator:last-child]:min-h-[60px] [&>.rh2-rating-indicator:last-child>.rh2-rating-label]:min-h-[60px] max-[1279px]:order-4 max-[1279px]:w-[310px] max-[680px]:order-5 max-[680px]:w-full">
        {benefits.map(([title, copy, icon]) => (
          <div
            className="rh2-rating-indicator relative flex min-h-[77px] w-[280px] items-center gap-2 bg-transparent p-0 max-[1279px]:w-full"
            key={title}
          >
            <span className="contents max-[1279px]:hidden">
              <RatingConnector />
            </span>
            <div className="rh2-rating-label flex min-h-[77px] w-60 items-center gap-3 rounded-xl bg-white p-3 shadow-[0_4px_6px_rgba(0,0,0,.03)] max-[1279px]:w-full [&>i]:grid [&>i]:size-8 [&>i]:flex-none [&>i]:place-items-center [&>i]:rounded-lg [&>i]:bg-[rgba(229,75,75,.08)] [&>span]:flex [&>span]:min-w-0 [&>span]:flex-auto [&>span]:flex-col [&>span]:gap-0.5 [&_b]:whitespace-nowrap [&_b]:text-sm [&_b]:leading-[17px] [&_b]:text-black/90 [&_small]:text-xs [&_small]:leading-[1.4] [&_small]:text-[#666572]">
              <i>
                <Image src={icon} alt="" width={16} height={16} />
              </i>
              <span>
                <b>{title}</b>
                <small>{copy}</small>
              </span>
            </div>
          </div>
        ))}
        <p className="max-w-[280px] text-xs leading-5 text-[var(--muted)] max-[1279px]:max-w-full">
          Illustration only. Trustpilot makes the final decision on every report, so removal is not guaranteed.
        </p>
      </div>
    </div>
  );
}
const riskCalculatorExample = {
  currentRating: 3.9,
  reviewCount: 106,
  removedReviewRating: 1,
  maximumRemovedReviews: 18,
};

  const formatRiskRating = (rating: number) => rating.toFixed(1);

function RiskCalculatorGraphic({
  negativeReviewCount,
  platformName,
}: {
  negativeReviewCount: number;
  platformName: string;
}) {
  const removedReviewCount = Math.min(
    riskCalculatorExample.maximumRemovedReviews,
    Math.max(0, Math.round(negativeReviewCount)),
  );
  const remainingReviewCount =
    Math.max(1, riskCalculatorExample.reviewCount - removedReviewCount);
  const projectedRating = Math.min(
    5,
    (riskCalculatorExample.currentRating * riskCalculatorExample.reviewCount -
      riskCalculatorExample.removedReviewRating * removedReviewCount) /
      remainingReviewCount,
  );
  const ratingImprovement =
    projectedRating - riskCalculatorExample.currentRating;
  const improvementPercentage = Math.round(
    (ratingImprovement / riskCalculatorExample.currentRating) * 100,
  );
  const activeSegmentCount = Math.round(
    (removedReviewCount / riskCalculatorExample.maximumRemovedReviews) * 14,
  );

  return (
    <div
      className="rh2-risk-calculator min-[641px]:!items-start min-[641px]:!pt-[51px] !bg-transparent !bg-none min-[1281px]:!w-[662px] max-[1100px]:!justify-self-center"
      aria-label="Illustration of a possible Trustpilot rating change"
      aria-live="polite"
    >
      <span
        className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-[28px]"
        aria-hidden="true"
      >
        <Image
          className="absolute left-[-45px] top-[-69px] h-[calc(100%+150px)] w-[calc(100%+92px)] max-w-none max-[800px]:top-[-17%] max-[800px]:h-[137%]"
          src={RiskCalculatorBackground}
          unoptimized
          alt=""
          width={754}
          height={566}
          sizes="(max-width: 702px) calc(100vw + 52px), 754px"
        />
      </span>
      <div className="risk-guarantee-badge w-max max-w-[calc(100%-16px)] absolute right-[18px] top-[-30px] z-[3] flex h-[38px] items-center gap-px whitespace-nowrap rounded-full bg-white py-0.5 pl-1 pr-3 font-jetbrains-mono text-xs font-bold tracking-[.25px] text-[#3c6e71] shadow-[0_4px_16px_rgba(60,110,113,.26)] max-[640px]:right-1/2 max-[640px]:translate-x-1/2 max-[640px]:text-[9px] max-[360px]:text-[8px]">
        <Image src={RiskShield} alt="" width={34} height={34} unoptimized />
        <span>PAY ONLY FOR REVIEWS REMOVED</span>
      </div>
      <div className="rh2-risk-rating-card max-sm:w-full relative z-[1] flex w-[calc(100%-56px)] max-w-[606.6px] flex-col items-start gap-[23.831px] rounded-[30.33px] border-[1.083px] border-black/[.06] bg-white px-[34.663px] pb-[30.33px] pt-[34.663px] shadow-[0_12.999px_17.331px_rgba(15,31,33,.1)] min-[1281px]:!w-[606.6px] max-[800px]:w-[calc(100%-28px)] max-[800px]:px-[18px] max-[520px]:gap-5 max-[520px]:px-3.5 max-[520px]:pb-5 max-[520px]:pt-6">
        <div className="rh2-risk-rating-duel grid w-full grid-cols-[minmax(0,38fr)_41.163px_minmax(0,49fr)] items-center gap-[15.165px] max-[520px]:grid-cols-1">
          <div className="is-current flex h-[137px] min-w-0 flex-col items-start justify-center gap-[8.666px] overflow-hidden rounded-[17.331px] border-[1.083px] border-black/[.06] bg-[#fdfdfd] px-[19.498px] py-[17.331px] text-[#8a8a98] [&>small]:font-jetbrains-mono [&>small]:text-[10.832px] [&>small]:font-normal [&>small]:leading-[16.248px] [&>small]:tracking-[1.7331px] [&>span]:leading-none [&>strong]:text-[41.162px] [&>strong]:font-[590] [&>strong]:leading-none [&>strong]:line-through [&>strong]:decoration-[#8a8a98]">
            <small>CURRENT</small>
            <strong>
              {formatRiskRating(riskCalculatorExample.currentRating)}
            </strong>
            <span>
              <IconReviewStarGenerator size={16} count={3} color="#8a8a98" />
            </span>
          </div>
          <i
            className="grid size-[41.163px] place-items-center rounded-full bg-[#3c6e711f] text-[21.664px] font-bold not-italic leading-none text-[#3c6e71] max-[520px]:mx-auto max-[520px]:my-[-8px] max-[520px]:rotate-90"
            aria-hidden="true"
          >
            →
          </i>
          <div className="is-improved relative flex min-h-[164px] min-w-0 flex-col items-start justify-center gap-[10.832px] overflow-hidden rounded-[17.331px] border-[1.083px] border-[#3c6e714d] bg-[#3c6e710a] px-[21.664px] py-[19.498px] max-[360px]:px-3.5 [&>b]:relative [&>b]:z-[1] [&>small]:relative [&>small]:z-[1] [&>small]:font-jetbrains-mono [&>small]:text-[10.832px] [&>small]:font-medium [&>small]:leading-normal [&>small]:tracking-[1.0832px] [&>span]:relative [&>span]:z-[1] [&>span]:flex [&>span]:w-full [&>span]:min-w-0 [&>span]:items-center [&>span]:gap-[10.832px] max-[360px]:[&>span]:gap-1.5 [&_em]:ml-auto [&_em]:shrink-0 [&_em]:whitespace-nowrap [&_em]:rounded-full [&_em]:bg-[#3c6e71] [&_em]:px-[12.999px] [&_em]:py-[6.499px] [&_em]:text-[16.248px] [&_em]:font-bold [&_em]:not-italic [&_em]:text-white [&_em]:shadow-[0_3.25px_10.832px_rgba(60,110,113,.35)] max-[360px]:[&_em]:px-2 max-[360px]:[&_em]:py-1.5 max-[360px]:[&_em]:text-xs [&_strong]:shrink-0 [&_strong]:text-[56.327px] [&_strong]:font-bold [&_strong]:leading-none [&_strong]:text-[#0a0a0b] max-[360px]:[&_strong]:text-[44px]">
            <div
              aria-hidden="true"
              className="absolute left-[159.44px] top-[-50.71px] z-0 size-[159.905px] rounded-full bg-[#3c6e7126] blur-[21.664px]"
            />
            <small>POTENTIAL AFTER</small>
            <span>
              <strong>{formatRiskRating(projectedRating)}</strong>
              <em>↑ {formatRiskRating(ratingImprovement)}</em>
            </span>
            <b>
              <IconReviewStarGenerator
                size={19}
                count={Math.round(projectedRating)}
              />
            </b>
          </div>
        </div>
        <div className="rh2-risk-improvement flex w-full flex-col gap-[10.832px]">
          <div className="flex items-center justify-between text-[15.165px] font-[510] leading-normal text-[#0a0a0b] [&>strong]:text-[17.331px] [&>strong]:font-[590] [&>strong]:text-[#3c6e71]">
            <span>Potential change</span>
            <strong>+{improvementPercentage} %</strong>
          </div>
          <div
            className="rh2-risk-segments grid h-[12.999px] w-full grid-cols-[repeat(14,1fr)] gap-[4.333px]"
            aria-hidden="true"
          >
            {Array.from({length: 14}, (_, index) => (
              <i
                className={`h-full rounded-[3.25px] ${index >= activeSegmentCount ? "bg-[#8a8a981f]" : index < 2 ? "is-red bg-[#f14336]" : index < 4 ? "is-yellow bg-[#ffac11]" : "is-green bg-[#3c6e71]"}`}
                key={index}
              />
            ))}
          </div>
        </div>
        <p className="w-full text-left text-[14.082px] leading-normal text-[#8a8a98]">
          Illustrative calculation based on a sample profile with 106 reviews.
        </p>
      </div>
    </div>
  );
}
function FooterContact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [mailMessage, setMailMessage] = useState("");

  const submitContact = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const body = [
      "Name: " + name.trim(),
      "Email: " + email.trim(),
      "",
      message.trim(),
    ].join(String.fromCharCode(10));
    window.location.href =
      "mailto:support@reputech.io?subject=" +
      encodeURIComponent("Question about Reputech") +
      "&body=" +
      encodeURIComponent(body);
    setMailMessage("Your email app should open with your message ready to send.");
  };

  return (
    <section
      id="contact"
      className="footer-section font-normal flex flex-col items-center gap-10 bg-[var(--bg)] py-[60px]"
    >
      <div className="footer-contact-card relative flex w-[calc(100%-32px)] max-w-[1296px] flex-col overflow-hidden rounded-[32px] bg-[#082924] bg-[url('/assets/images/landing-redesign/figma-qa/footer-background.webp')] bg-cover bg-center text-white sm:w-[calc(100%-48px)] min-[1280px]:h-[590px] min-[1280px]:block">
        <div className="footer-contact-content relative z-10 px-6 pt-12 sm:px-12 min-[1280px]:absolute min-[1280px]:left-20 min-[1280px]:top-1/2 min-[1280px]:w-[525px] min-[1280px]:-translate-y-1/2 min-[1280px]:p-0">
          <h2 className="text-[32px] font-semibold leading-[1.2] tracking-[-1px] sm:text-5xl sm:leading-[1.2]">
            Let’s talk about your Trustpilot profile.
          </h2>
          <p className="mt-2.5 max-w-[525px] text-base leading-6 text-white/70">
            Have a question about the process or your request? Send us a note and our team will get back to you.
          </p>
          <p className="footer-contact-promise font-jetbrains-mono mt-7 text-sm leading-6 text-white/80">
            Or email us directly at{" "}
            <a className="underline underline-offset-4" href="mailto:support@reputech.io">
              support@reputech.io
            </a>
          </p>
          <form
            onSubmit={submitContact}
            className="footer-contact-form mt-8 grid gap-3 sm:grid-cols-2 min-[1280px]:mt-10 [&_input]:h-14 [&_input]:w-full [&_input]:rounded-2xl [&_input]:border [&_input]:border-white/15 [&_input]:bg-black/20 [&_input]:px-5 [&_input]:text-white [&_input]:backdrop-blur-sm [&_input]:placeholder:text-white/55 [&_input]:focus-visible:outline [&_input]:focus-visible:outline-2 [&_input]:focus-visible:outline-white [&_textarea]:w-full [&_textarea]:rounded-2xl [&_textarea]:border [&_textarea]:border-white/15 [&_textarea]:bg-black/20 [&_textarea]:px-5 [&_textarea]:py-4 [&_textarea]:text-white [&_textarea]:placeholder:text-white/55 [&_textarea]:focus-visible:outline [&_textarea]:focus-visible:outline-2 [&_textarea]:focus-visible:outline-white"
          >
            <label className="min-w-0">
              <span className="sr-only">Name</span>
              <input
                autoComplete="name"
                onChange={(event) => setName(event.target.value)}
                placeholder="Name"
                required
                value={name}
              />
            </label>
            <label className="min-w-0">
              <span className="sr-only">Email</span>
              <input
                autoComplete="email"
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Email"
                required
                type="email"
                value={email}
              />
            </label>
            <label className="min-w-0 sm:col-span-2">
              <span className="sr-only">How can we help?</span>
              <textarea
                className="min-h-28"
                onChange={(event) => setMessage(event.target.value)}
                placeholder="How can we help?"
                required
                rows={4}
                value={message}
              />
            </label>
            <button
              type="submit"
              className="flex h-12 items-center justify-between rounded-full bg-[#3c6e71] py-2 pl-5 pr-2 text-base font-semibold sm:col-span-2"
            >
              Open email app{" "}
              <Image src={FooterSubmitArrow} alt="" width={32} height={32} />
            </button>
          </form>
          {mailMessage && (
            <p className="mt-3 text-sm text-white/80" role="status">
              {mailMessage}
            </p>
          )}
        </div>
        <div className="footer-advisor relative mt-8 h-[470px] w-full sm:h-[550px] min-[1280px]:absolute min-[1280px]:-top-[43.75px] min-[1280px]:right-[65px] min-[1280px]:mt-0 min-[1280px]:h-[855px] min-[1280px]:w-[639px]">
          <Image
            src={FooterImage}
            alt="Customer support adviser"
            fill
            sizes="(max-width: 640px) 100vw, 639px"
            unoptimized
            className="object-cover object-top sm:object-contain min-[1280px]:object-cover"
          />
        </div>
      </div>
    </section>
  );
}

export function ReputechLandingPage() {
  const processSteps = getProcessSteps();
  const trustpilotPriceTier = useSelector(selectPriceTierTrustPilot);
  const pricingTiers = getMarketingPricingTiers(trustpilotPriceTier);
  const defaultPricingTierIndex = pricingTiers.length - 1;
  const [menuOpen, setMenuOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(0);
  const [faqTab, setFaqTab] = useState(0);
  const [negativeReviewCount, setNegativeReviewCount] = useState(8);
  const [requestedPricingTierIndex, setRequestedPricingTierIndex] = useState<
    number | null
  >(null);
  const selectedPricingTierIndex = Math.min(
    requestedPricingTierIndex ?? defaultPricingTierIndex,
    Math.max(0, pricingTiers.length - 1),
  );
  const selectedPricingTier = pricingTiers[selectedPricingTierIndex];
  const displayedFaqCategories = faqCategories.map((category) => ({
    ...category,
    items: category.items.map(([question, answer]) =>
      question === "How much does the service cost?"
        ? [
            question,
            `The per-review rate depends on the number of reviews in one order for one Trustpilot profile: ${pricingTiers.map(({range, price}) => `${range}: ${price} per review`).join(", ")}.`,
          ]
        : [question, answer],
    ),
  }));
  const faqTabsSwiperRef = useRef<SwiperCore | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const {scrollYProgress: timelineScrollProgress} = useScroll({
    target: timelineRef,
    offset: ["start 75%", "end 25%"],
  });
  const scrollTo = (id: string) =>
    document.getElementById(id)?.scrollIntoView({behavior: "smooth"});
  const riskSliderProgress =
    (negativeReviewCount / riskCalculatorExample.maximumRemovedReviews) * 100;

  return (
    <>
      <section
        id="hero"
        className="hero-section relative isolate min-h-[820px] bg-[#f8fbfb] text-[var(--text)] [[data-theme=dark]_&]:bg-[#101817] max-[1100px]:min-h-0"
      >
        <div
          className="hero-background pointer-events-none absolute inset-0 -z-20 overflow-hidden [[data-theme=dark]_&]:opacity-20"
          aria-hidden="true"
        >
          <Image
            src={HeroBackground}
            alt=""
            unoptimized
            priority
            className="absolute inset-0 h-full w-full max-w-full object-fill"
          />
        </div>
        <div
          className="hero-artwork pointer-events-none absolute inset-0 -z-10 overflow-hidden max-[1100px]:hidden"
          aria-hidden="true"
        >
          <div className="absolute left-1/2 top-0 h-[820px] w-[1440px] -translate-x-1/2">
            <Image
              src={HeroConnector}
              alt=""
              className="absolute left-[-97px] top-[396.5px] h-[357px] w-[1557px] max-w-none"
            />
            <div className="absolute left-[100.92px] top-[280.16px] h-[198.72px] w-[161.63px] overflow-hidden rounded-[13.25px] shadow-[0_4.67px_16.8px_rgba(0,0,0,.2)]">
              <Image
                src={HeroBusinessman}
                alt=""
                className="absolute left-[-33.91%] top-0 h-full w-[183.84%] max-w-none"
                sizes="298px"
                unoptimized
                priority
              />
            </div>
            <div className="absolute left-[114.43px] top-[452px] w-[263.22px]">
              <HeroRatingCard />
            </div>
            <div className="absolute left-[1115px] top-[178px] h-[179px] w-36 overflow-hidden rounded-[20px] shadow-[0_7.04px_25.36px_rgba(0,0,0,.2)]">
              <Image
                src={HeroTradesman}
                alt=""
                className="h-full w-full object-cover object-right"
                sizes="144px"
                priority
              />
            </div>
            <div className="absolute left-[1219px] top-[271px] h-[116px] w-[118px] overflow-hidden rounded-[20px] shadow-[0_7.04px_25.36px_rgba(0,0,0,.2)]">
              <Image
                src={HeroDoctor}
                alt=""
                className="absolute left-[-48.32%] top-[0.46%] h-full w-[174.36%] max-w-none"
                sizes="206px"
                unoptimized
                priority
              />
            </div>
            <div className="absolute left-[1099.63px] top-[496.37px] h-[160.68px] w-[124.69px] overflow-hidden rounded-[16.74px] shadow-[0_5.9px_21.22px_rgba(0,0,0,.2)]">
              <Image
                src={HeroBusinesswoman}
                alt=""
                className="absolute -left-[22.88%] -top-[20.64%] h-[181.58%] w-[156.4%] max-w-none"
                sizes="195px"
              />
            </div>
            <div className="absolute left-[1174.11px] top-[621.06px] flex h-[79.55px] w-[169.78px] flex-col gap-[11.32px] rounded-[6.59px] bg-white p-[10.54px] text-black shadow-[0_0_18.11px_rgba(0,79,121,.12)]">
              <div className="flex items-center gap-[7.9px]">
                <Image
                  src={ReviewerLiana}
                  alt=""
                  className="size-[30.18px] rounded-full object-cover"
                />
                <div className="flex-1 text-[9.22px] leading-[12px]">
                  <span className="font-medium">Liana M.</span>
                  <p className="mt-1 text-black/40">3 weeks ago</p>
                </div>
                <div className="flex gap-[2.63px]">
                  <Image src={ReviewStar} alt="" className="size-[15.8px]" />
                  <Image src={ReviewStar} alt="" className="size-[15.8px]" />
                </div>
              </div>
              <div className="flex flex-col gap-[5.66px]">
                <span className="h-[5.66px] rounded-full bg-black/[.04]" />
                <div className="flex gap-[5.66px]">
                  <span className="h-[5.66px] w-12 rounded-full bg-black/[.04]" />
                  <span className="h-[5.66px] flex-1 rounded-full bg-black/[.04]" />
                </div>
              </div>
            </div>
            <span className="absolute left-[1209.26px] top-[577.54px] rounded-full bg-[#bbe4c2] px-[15px] py-[6.48px] text-[15.12px] font-medium leading-[22.68px] text-black shadow-[0_5.9px_10.61px_rgba(0,0,0,.2)]">
              Report submitted
            </span>
            <span className="absolute left-[1251.94px] top-[502.23px] grid size-[47.37px] place-items-center rounded-full bg-white shadow-[0_5.9px_10.61px_rgba(0,0,0,.2)]">
              <Image src={ReviewTrash} alt="" className="size-[26.32px]" />
            </span>
          </div>
        </div>
        <div className="rh2-guarantee">
          <Image
            src={GuaranteeIcon}
            alt=""
            width={18}
            height={19}
            aria-hidden="true"
          />
          No upfront charge <span>•</span> Card required before submission
        </div>
        <header className="hero-header relative z-30 -mt-[3px] border border-black/[.06] bg-white/90 shadow-[0_2px_10px_rgba(0,0,0,.05)] backdrop-blur-md [[data-theme=dark]_&]:bg-[#161817]/90">
          <div className="relative mx-auto flex h-[62px] max-w-[1200px] items-center justify-between gap-5 max-[1240px]:mx-5 max-[520px]:mx-3 max-[520px]:gap-2">
            <a
              href="/"
              className="hero-brand flex shrink-0 items-center"
              aria-label="Reputech home"
            >
              <Image
                src="/brand/logo.svg"
                alt="Reputech"
                width={166}
                height={30}
                priority
                className="h-[30px] w-[166px] max-[520px]:w-[145px]"
              />
            </a>
            <nav
              id="hero-navigation"
              aria-label="Main navigation"
              className={`hero-navigation items-center gap-8 text-[15px] font-medium min-[1101px]:flex max-[1100px]:absolute max-[1100px]:left-0 max-[1100px]:right-0 max-[1100px]:top-full max-[1100px]:flex-col max-[1100px]:items-stretch max-[1100px]:gap-0 max-[1100px]:rounded-xl max-[1100px]:border max-[1100px]:border-[var(--line)] max-[1100px]:bg-[var(--surface)] max-[1100px]:p-3 max-[1100px]:shadow-lg max-[1100px]:[&>*]:p-3 ${menuOpen ? "flex" : "max-[1100px]:hidden"}`}
            >
              {[
                ["Services", "impact"],
                ["Process", "process"],
                ["Pricing", "pricing"],
                ["FAQ", "faq"],
              ].map(([label, id]) => (
                <a
                  key={label}
                  href={id === "#" ? "#" : `#${id}`}
                  onClick={() => setMenuOpen(false)}
                >
                  {label}
                </a>
              ))}
            </nav>
            <div className="hero-header-actions flex shrink-0 items-center gap-4 max-[1100px]:ml-auto max-[520px]:gap-2">
              <LandingThemeToggle />
              <button
                className="hero-header-cta flex h-12 items-center gap-2.5 rounded-full bg-[#3c6e71] px-3.5 !text-white max-[680px]:hidden"
                onClick={() => scrollTo("start")}
              >
                <span className="text-base font-semibold leading-none">
                  Start order
                </span>
                <span className="grid size-7 place-items-center rounded-full bg-white/15">
                  <Image
                    src={ArrowRightIcon}
                    alt=""
                    width={14}
                    height={14}
                    className="size-3.5 brightness-0 invert"
                  />
                </span>
              </button>
            </div>
            <button
              className="hero-menu grid size-10 shrink-0 content-center gap-1 rounded-lg border border-[var(--line)] p-2.5 min-[1101px]:hidden"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="hero-navigation"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <span className="h-px bg-current" />
              <span className="h-px bg-current" />
              <span className="h-px bg-current" />
            </button>
          </div>
        </header>
        <div className="hero-content relative z-10 mx-auto flex max-w-[629px] flex-col items-center px-5 pt-20 max-[1100px]:pt-16 max-[520px]:pt-12">
          <div className="hero-intro flex w-full max-w-[531px] flex-col items-center gap-[17px] text-center">
            <h1 className="hero-title text-[48px] font-semibold leading-[52px] tracking-normal max-[580px]:text-[38px] max-[580px]:leading-[43px] max-[360px]:text-[34px]">
              Unfair Trustpilot reviews?{" "}
              <span className="text-[#3c6e71] [[data-theme=dark]_&]:text-[#7fe8d4]">
                We can help.
              </span>
            </h1>
            <p className="hero-description text-sm leading-[22px] tracking-[-.28px] text-[#666572] [[data-theme=dark]_&]:text-[var(--muted)]">
              Find your Trustpilot profile, choose the reviews you want assessed, and follow every step of your report.
            </p>
          </div>
          <div className="hero-search mt-6 w-full max-w-[500px]">
            <ReputechSearchForm submitLabel="Search" />
            <ul className="hero-benefits mt-[18px] flex flex-wrap items-center justify-center gap-x-[25px] gap-y-2 text-[13px] leading-5 text-[var(--muted)] max-[580px]:gap-x-3">
              {[
                "No upfront charge",
                "Review before you submit",
                "Card required before submission",
              ].map((label) => (
                <li key={label} className="flex items-center gap-2">
                  <HeroBenefitCheck />
                  {label}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="hero-proof relative z-0 mx-auto mt-[89px] flex max-w-[700px] flex-col items-center gap-8 px-5 pb-24 max-[1100px]:mt-16 max-[1100px]:pb-16 max-[520px]:mt-12">
          <div className="hero-statistics flex items-center gap-5 text-center max-[580px]:flex-wrap max-[580px]:justify-center">
            {[
              ["No upfront charge", "Card saved before submission"],
              ["One profile", "Per order pricing"],
              ["Pay on success", "Only removed reviews"],
            ].map(([value, label], index) => (
              <div
                key={label}
                className={`flex flex-col items-center gap-2 ${index ? "border-l border-[var(--line)] pl-5 max-[580px]:border-0 max-[580px]:pl-0" : ""}`}
              >
                <b className="text-xl font-semibold leading-5">{value}</b>
                <span className="font-jetbrains-mono text-[10px] uppercase leading-[15px] tracking-[1.6px] text-[var(--muted)]">
                  {label}
                </span>
              </div>
            ))}
          </div>
          <div className="hero-payments flex flex-wrap items-center justify-center gap-6">
            <span className="font-jetbrains-mono text-[11px] font-bold uppercase leading-[16.5px]">
              Card payments
            </span>
            <CardBrandLogos />
          </div>
        </div>
      </section>

      <section
        id="impact"
        className={`${sectionClass} rh2-impact isolate !h-auto !min-h-0 overflow-hidden !bg-[linear-gradient(180deg,#f9fbfb_37.065%,#3c6e71_77.067%)] !pt-[60px] before:!content-none [[data-theme=dark]_&]:!bg-[linear-gradient(180deg,#101817_37.065%,#3c6e71_77.067%)] max-[800px]:!pt-12`}
      >
        <div
          className="impact-background pointer-events-none !absolute inset-0 -z-10 overflow-hidden"
          aria-hidden="true"
        >
          <Image
            src={ImpactGridHorizontal}
            alt=""
            className="absolute left-[calc(50%+15.89px)] top-[342px] h-[1482.23px] w-[1715.55px] max-w-none -translate-x-1/2 -translate-y-1/2 opacity-[.04]"
          />
          <Image
            src={ImpactGridVertical}
            alt=""
            className="absolute left-[calc(50%+15.88px)] top-[337.52px] h-[1646.92px] w-[1701.18px] max-w-none -translate-x-1/2 -translate-y-1/2 rotate-90 opacity-[.04]"
          />
          <div className="absolute inset-x-0 top-0 h-[273px] bg-gradient-to-b from-[#f8fbfb] to-transparent [[data-theme=dark]_&]:from-[#101817]" />
        </div>
        <div className={headingClass}>
          <p className={kickerClass}>Why it matters</p>
          <h2>
            One bad review
            <br />
            can cost you <span>real customers.</span>
          </h2>
          <p>
            Your rating shapes first impressions, often before someone visits your website. Here is what the research says about negative reviews.
          </p>
        </div>
        <div
          className="rh2-impact-grid grid grid-cols-3 gap-5 min-[801px]:mx-auto min-[801px]:w-full min-[801px]:max-w-[1280px] max-[800px]:grid-cols-1 max-[520px]:gap-4"
          aria-label="How negative reviews can affect a business"
        >
          {[
            {
              value: "88%",
              copy: (
                <>
                  People read reviews before
                  <br className="hidden min-[1200px]:block" /> choosing a business
                </>
              ),
              asset: ImpactReviews,
              darkAsset: ReadReviews,
            },
            {
              value: "−70%",
              copy: (
                <>
                  Fewer enquiries for profiles rated
                  <br className="hidden min-[1200px]:block" /> below 4.2 stars
                </>
              ),
              asset: ImpactChart,
              darkAsset: Below4Rating,
            },
            {
              value: "1 in 3",
              copy: (
                <>
                  Leave after seeing a negative review
                </>
              ),
              asset: BadReviewTrustpilot,
              darkAsset: BadReviewTrustpilot,
            },
          ].map((item, index) => (
            <article
              className="impact-card relative flex min-w-0 flex-col justify-between overflow-hidden rounded-[20px] bg-white after:pointer-events-none after:absolute after:inset-0 after:z-10 after:rounded-[inherit] after:border after:border-[#3c6e714d] max-[800px]:mx-auto max-[800px]:w-full max-[800px]:max-w-[480px] max-[520px]:rounded-2xl"
              key={item.value}
            >
              <div className="rh2-impact-copy flex min-h-[198px] shrink-0 flex-col justify-center gap-2.5 p-8 min-[1200px]:h-[198px] max-[1100px]:p-6 max-[800px]:min-h-0 max-[520px]:gap-1.5 max-[520px]:p-5">
                <strong className="!bg-[linear-gradient(20deg,#3c6e71_7.27%,#72d1d7_60.43%)] bg-clip-text !text-[64px] font-medium !leading-[76px] tracking-normal text-transparent [-webkit-text-fill-color:transparent] max-[1100px]:!text-5xl max-[1100px]:!leading-[58px] max-[520px]:!text-[44px] max-[520px]:!leading-[53px]">
                  {item.value}
                </strong>
                <p className="max-w-none text-base leading-6 text-[#666572] max-[520px]:text-sm max-[520px]:leading-5">
                  {item.copy}
                </p>
              </div>
              <div
                className="rh2-impact-graphic relative aspect-[413/214] shrink-0 overflow-hidden bg-[linear-gradient(180deg,#fff_0%,#e9fafa_59.753%)] text-inherit"
                aria-hidden="true"
              >
                <Image
                  // className="rh2-impact-asset px-8 max-[1100px]:p-6 max-[520px]:p-5 is-light absolute inset-0 block size-full object-contain"
                  className={classNames(
                    "rh2-impact-asset is-light absolute inset-0 block size-full object-contain",
                    index === 2 && "px-5 max-[1100px]:px-3 max-[520px]:px-2 "
                  )}
                  src={item.asset}
                  alt=""
                  unoptimized
                  sizes="(max-width: 520px) calc(100vw - 40px), (max-width: 800px) 480px, (max-width: 1328px) calc((100vw - 88px) / 3), 414px"
                />
                <Image
                  src={item.darkAsset}
                  alt=""
                  unoptimized
                  className="is-dark absolute inset-0 size-full object-contain"
                  sizes="(max-width: 520px) calc(100vw - 40px), (max-width: 800px) 480px, 414px"
                />
              </div>
            </article>
          ))}
        </div>
      </section>

      <section
        id="process"
        className={`${sectionClass} rh2-process isolate !min-h-0 overflow-hidden !pb-20 before:!content-none !bg-[linear-gradient(180deg,#3c6e71_0%,#163e3f_6.698%,#002121_15.985%,#101e1f_53.834%,#0f0f0f_100%)] max-[800px]:!py-16`}
      >
        <div
          className="process-background pointer-events-none !absolute inset-0 -z-10 overflow-hidden"
          aria-hidden="true"
        >
          <Image
            src={ProcessGridHorizontal}
            alt=""
            className="absolute left-[calc(50%+15.89px)] top-[342px] h-[1482.23px] w-[1715.55px] max-w-none -translate-x-1/2 -translate-y-1/2 opacity-[.014]"
          />
          <Image
            src={ProcessGridVertical}
            alt=""
            className="absolute left-[calc(50%+15.89px)] top-[342px] h-[1482.23px] w-[1715.55px] max-w-none -translate-x-1/2 -translate-y-1/2 rotate-90 opacity-[.014]"
          />
        </div>
        <div className="rh2-heading process-heading mx-auto mb-20 flex max-w-[914px] flex-col items-center gap-4 text-center text-white max-[800px]:mb-12">
          <p className="font-jetbrains-mono text-[11px] font-medium uppercase leading-[16.5px] tracking-[3.08px] text-white">
            How it works
          </p>
          <h2 className="w-full text-[48px] font-semibold leading-[48px] max-[800px]:text-[clamp(34px,9vw,44px)] max-[800px]:leading-[1.05]">
            Four steps to a clearer profile
          </h2>
          <p className="w-[452px] max-w-full text-lg leading-[26px] text-white/60 max-[800px]:text-base">
            From your first search to the report submission, the process is clear and handled online.
          </p>
        </div>
        <div
          className="rh2-timeline relative mx-auto flex max-w-[1247.15px] flex-col gap-[50px] md:gap-[100px] md:pl-[63px]"
          ref={timelineRef}
        >
          <TimelineProgressRail progress={timelineScrollProgress} />
          {processSteps.map((step, index) => (
            <article
              className="process-step flex w-full min-w-0 flex-col gap-8 min-[1200px]:grid min-[1200px]:grid-cols-[minmax(0,1fr)_minmax(0,606px)] min-[1200px]:items-center min-[1200px]:gap-[clamp(40px,8.333vw,120px)]"
              key={step.roman}
            >
              <div className="process-step-content flex w-full min-w-0 flex-col justify-center gap-5">
                <div className="process-step-label flex items-center gap-3 font-jetbrains-mono text-xs font-medium leading-[18px] text-custom-brand-light-green">
                  <span className="relative size-5 shrink-0" aria-hidden="true">
                    <Image
                      src={ProcessStepMarker}
                      alt=""
                      className="absolute -left-2 -top-1 size-9 max-w-none"
                    />
                  </span>
                  STEP {step.roman}
                </div>
                <div className="flex flex-col gap-2">
                  <h3 className="text-[28px] font-semibold leading-[42px] tracking-[-0.28px]">
                    {step.title}
                  </h3>
                  <p className="text-base leading-[24.38px] tracking-normal text-white/60">
                    {step.copy}
                  </p>
                </div>
                {step.note && (
                  <small className="process-step-note flex items-start gap-1.5 font-jetbrains-mono text-sm font-medium leading-[18px] text-custom-brand-light-green">
                    <span
                      className="mt-[7px] size-1 shrink-0 rounded-full bg-current"
                      aria-hidden="true"
                    />
                    {step.note}
                  </small>
                )}
                {index === processSteps.length - 1 && (
                  <div className="process-search relative z-10 w-full min-w-0 max-w-[446px] max-[1199px]:mx-auto max-[1199px]:max-w-[606px]">
                    <ReputechSearchForm submitLabel="Search" />
                  </div>
                )}
              </div>
              <div className="process-step-preview isolate w-full min-w-0 max-[1199px]:mx-auto max-[1199px]:max-w-[606px]">
                <BrowserDemo step={index + 1} />
              </div>
            </article>
          ))}
        </div>
      </section>

      <section
        className={`${sectionClass} rating-comparison-section rh2-rating max-[1279px]:!h-auto`}
      >
        <div className={headingClass}>
          <p className={kickerClass}>Rating calculator</p>
          <h2 className="max-[680px]:!leading-[1.15]">
            See a <span>possible rating change</span>
          </h2>
          <p>
            This illustration shows how a profile rating could change if Trustpilot removes reported reviews. Results vary, and Trustpilot makes the final decision.
          </p>
        </div>
        <RatingComparison />
      </section>

      <section
        id="pricing"
        className={`${sectionClass} pricing-section rh2-pricing !pb-[120px] max-[800px]:!pb-16`}
      >
        <div className={headingClass}>
          <p className={kickerClass}>Trustpilot review removal</p>
          <h2>
            Review removal, rethought:<br />
            <span>Clear, fair pricing.</span>
          </h2>
          <p>
            A straightforward process with clear per-review pricing. No upfront charge, and you pay only for reviews Trustpilot removes.
          </p>
        </div>
        <div className="rh2-pricing-panel relative !overflow-hidden !rounded-[36px] !bg-transparent !bg-none !shadow-[0_50px_100px_-45px_rgba(4,25,22,0.85)] max-[800px]:!rounded-3xl">
          <Image
            className="pointer-events-none absolute left-[-55px] top-[-5px] z-0 h-[calc(100%+111px)] w-[calc(100%+110px)] max-w-none max-[800px]:top-[-1%] max-[800px]:h-[118%]"
            src={PricingPanelBackground}
            unoptimized
            alt=""
            width={1390}
            height={752}
            sizes="(max-width: 1328px) calc(100vw + 62px), 1390px"
            aria-hidden="true"
          />
          <div className="rh2-benefit-grid relative z-[1] [&_.rh2-check]:!size-6 [&_.rh2-check]:!basis-6 [&_.rh2-check]:!bg-transparent [&_.rh2-check_img]:!size-6 [&_h3]:!font-medium [&_p]:!font-normal">
            {[
              [
                "A clear process",
                "See each step from profile search to report submission.",
              ],
              [
                "Card required",
                "Save a card before we submit your request.",
              ],
              [
                "Transparent pricing",
                "Review your selections and order details before submitting.",
              ],
              [
                "Pay on success",
                "No charge for reviews Trustpilot does not remove.",
              ],
            ].map(([title, copy]) => (
              <article key={title}>
                <Check />
                <span>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </span>
              </article>
            ))}
          </div>
          <div className="rh2-price-grid relative z-[1] min-w-0">
            <article className="rh2-tiers min-w-0 w-full [&>h3]:font-bold [&>button:first-of-type]:!mt-2 [&>small]:!mt-3 [&>small]:!font-normal [&>small]:!tracking-[1.47px] max-[520px]:!p-4">
              <h3>Volume pricing</h3>
              <p>The per-review rate applies to every review in each order for one profile.</p>
              {pricingTiers.length === 0 ? (
                <div
                  className="grid min-h-[262px] place-items-center text-sm text-[#666572]"
                  role="status"
                >
                  Pricing is loading…
                </div>
              ) : (
                pricingTiers.map(({range, price}, index) => (
                  <button
                    aria-pressed={selectedPricingTierIndex === index}
                    className={`${selectedPricingTierIndex === index ? "is-active" : ""} min-w-0 w-full cursor-pointer text-left [font:inherit] transition-[border-color,background-color,color,box-shadow] duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3c6e71] max-[520px]:!grid-cols-[28px_minmax(0,1fr)_auto] max-[520px]:!gap-2 max-[520px]:!px-2.5 max-[520px]:!py-3 max-[520px]:[&>i]:!size-7`}
                    key={range}
                    onClick={() => setRequestedPricingTierIndex(index)}
                    type="button"
                  >
                    <i>{index + 1}</i>
                    <span className="min-w-0 max-[520px]:text-[15px] max-[520px]:leading-tight max-[360px]:text-sm">
                      {range}
                    </span>
                    <b className="shrink-0 whitespace-nowrap max-[520px]:!text-base">
                      {price}
                    </b>
                  </button>
                ))
              )}
              <small className="font-jetbrains-mono">
                NO UPFRONT CHARGE · PAY ON SUCCESS
              </small>
            </article>
            <article className="rh2-featured relative !overflow-hidden !rounded-[28px] !border-[3px] !border-[#3c6e71] !bg-transparent !p-[21px] !shadow-[0_40px_90px_-40px_rgba(4,25,22,0.7)] [&>ul]:!mb-7 [&>ul]:!mt-7 [&>ul]:font-medium [&>em]:!w-full [&>em]:!whitespace-normal [&>em]:!text-center [&>em]:!font-normal [&>em]:!tracking-[1.76px] max-[520px]:!p-4">
              <Image
                className="pointer-events-none !absolute left-[-50px] top-[-10px] z-0 h-[calc(100%+101px)] w-[calc(100%+100px)] max-w-none max-[800px]:top-[-2.7%] max-[800px]:h-[126%]"
                src={PricingFeaturedBackground}
                unoptimized
                alt=""
                width={686}
                height={504}
                sizes="(max-width: 800px) calc(100vw + 60px), 686px"
                aria-hidden="true"
              />
              {selectedPricingTier ? (
                <>
                  <small className="relative z-[1] font-jetbrains-mono">
                    <Image
                      src={FeaturedSparkleIcon}
                      alt=""
                      width={16}
                      height={16}
                    />{" "}
                    FEATURED RATE · 50+ REVIEWS
                  </small>
                  <strong className="relative z-[1] max-[520px]:!text-[36px]">
                    From {pricingTiers[2]?.price ?? "€100"}
                  </strong>
                </>
              ) : (
                <div
                  className="relative z-[1] grid min-h-[99px] place-items-center text-sm text-white/70"
                  role="status"
                >
                  Pricing is loading…
                </div>
              )}
              <p className="relative z-[1]">
                per removed review. The rate applies to every review in one order for one profile.
              </p>
              <ul className="relative z-[1]">
                <li>
                  <span className="grid size-5 shrink-0 place-items-center rounded-full bg-white">
                    <Image
                      src={PricingCheckIcon}
                      alt=""
                      width={16}
                      height={16}
                    />
                  </span>
                  No upfront charge. Pay only after removal.
                </li>
                <li>
                  <span className="grid size-5 shrink-0 place-items-center rounded-full bg-white">
                    <Image
                      src={PricingCheckIcon}
                      alt=""
                      width={16}
                      height={16}
                    />
                  </span>
                  Card required before we submit your request.
                </li>
                <li>
                  <span className="grid size-5 shrink-0 place-items-center rounded-full bg-white">
                    <Image
                      src={PricingCheckIcon}
                      alt=""
                      width={16}
                      height={16}
                    />
                  </span>
                  Follow your request as it progresses.
                </li>
              </ul>
              <em className="relative z-[1] font-jetbrains-mono">
                NO UPFRONT CHARGE · PAY ONLY AFTER REMOVAL
              </em>
              <button
                className="relative z-[1]"
                onClick={() => scrollTo("start")}
              >
                Start your order
              </button>
            </article>
          </div>
        </div>
      </section>

      <section
        id="risk"
        className={`${sectionClass} null-risk-section rh2-risk !overflow-visible focus-within:z-40 min-[1281px]:!px-[max(24px,calc((100vw-1324px)/2))] min-[1281px]:!grid-cols-[minmax(0,618px)_662px] max-[1100px]:!grid-cols-1 max-[1100px]:gap-12`}
      >
        <div className="rh2-risk-copy min-w-0 w-full max-w-full [&>h2]:max-w-[579px] [&>ul]:!grid-cols-[minmax(0,338fr)_minmax(0,241fr)] [&>ul]:!gap-x-0 [&>ul]:!gap-y-4 [&>ul]:!mb-6 [&>ul>li]:!gap-3 [&_.rh2-check_img]:!size-3 max-[680px]:[&>ul]:!grid-cols-1">
          <p className={kickerClass}>Pay only on success</p>
          <h2>
            Zero risk, <span>full control</span>
          </h2>
          <p>
            Choose the reviews you want assessed and see your order details before submission. We charge only for reviews Trustpilot removes.
          </p>
          <ul>
            {[
              "No charge for reviews not removed",
              "Updates as your request progresses",
              "Clear per-review pricing",
              "Explore a sample rating change",
            ].map((x) => (
              <li key={x}>
                <Check />
                {x}
              </li>
            ))}
          </ul>
          <div className="rh2-risk-calculator-tablet mt-10 mb-7 hidden max-[1100px]:grid max-[1100px]:justify-items-center">
            <RiskCalculatorGraphic
              negativeReviewCount={negativeReviewCount}
              platformName="Trustpilot"
            />
          </div>
          <div className="rh2-risk-slider [&>div>strong]:!text-[30px] [&>div>strong]:!leading-[30px] !mb-4 [&>div]:!mb-4 [&>small]:!mt-4 [&>small]:font-jetbrains-mono max-[1100px]:mx-auto max-[1100px]:w-full max-[1100px]:max-w-none max-[1100px]:min-w-0">
            <div>
              <b>Reviews selected</b>
              <strong>{negativeReviewCount}</strong>
            </div>
            <span
              style={
                {
                  "--risk-slider-progress": `${riskSliderProgress}%`,
                } as CSSProperties
              }
            >
              <i aria-hidden="true" />
              <input
                aria-label="Reviews selected"
                aria-valuetext={`${negativeReviewCount} reviews selected`}
                max={riskCalculatorExample.maximumRemovedReviews}
                min={0}
                onChange={(event) =>
                  setNegativeReviewCount(Number(event.target.value))
                }
                step={1}
                type="range"
                value={negativeReviewCount}
              />
            </span>
            <small>
              <em>0</em>
              <em>{riskCalculatorExample.maximumRemovedReviews}</em>
            </small>
            <p className="mt-2 text-sm text-[var(--muted)]" aria-live="polite">
              Sample per-review rate for an order of {negativeReviewCount} reviews: {pricingTiers.find((tier) => negativeReviewCount >= tier.min_reviews && (tier.max_reviews === null || negativeReviewCount <= tier.max_reviews))?.price ?? "€150"} per review.
            </p>
          </div>
          <div className="rh2-risk-search-demo relative z-10 box-border !max-w-[579px] !pb-6 !bg-[linear-gradient(180deg,#d1efec,transparent_29%)] min-[801px]:-skew-x-1 max-[1100px]:mx-auto max-[1100px]:w-full max-[1100px]:max-w-none max-[1100px]:min-w-0 [&_.rh-inline-search-form]:min-w-0 [&_.rh-inline-search-form]:w-full [&_.rh-inline-search-form]:max-w-full">
            <h3>Find your business</h3>
            <p className="mb-5 text-center text-sm leading-[22px] text-[var(--muted)]">
              Enter a business name or Trustpilot profile URL to get started.
            </p>
            <ReputechSearchForm submitLabel="Search" />
            <p className="mt-4 text-center text-sm leading-[22px] text-[var(--muted)]">
              A card is required before submission. We charge only after Trustpilot removes a review.
            </p>
          </div>
        </div>
        <div className="grid justify-items-center max-[1100px]:hidden">
          <RiskCalculatorGraphic
            negativeReviewCount={negativeReviewCount}
            platformName="Trustpilot"
          />
        </div>
      </section>

      <section
        id="start"
        className={`${sectionClass} free-assessment-section rh2-cta !h-auto !min-h-0 focus-within:z-40`}
      >
        <div className="rh2-cta-card !h-auto !w-full !max-w-[1280px] overflow-visible [&_.rh2-kicker]:!text-white [&_.rh2-start-label]:!text-white">
          <div
            className="cta-grid pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
            aria-hidden="true"
          >
            <Image
              src={CtaGridHorizontal}
              alt=""
              className="absolute left-[calc(50%+15.89px)] top-[342.09px] h-[1482.23px] w-[1715.55px] max-w-none -translate-x-1/2 -translate-y-1/2 opacity-[.03]"
            />
            <Image
              src={CtaGridVertical}
              alt=""
              className="absolute left-[calc(50%+15.89px)] top-[342.09px] h-[1482.23px] w-[1715.55px] max-w-none -translate-x-1/2 -translate-y-1/2 rotate-90 opacity-[.03]"
            />
          </div>
          <div className="rh2-cta-decoration is-left pointer-events-none absolute left-[31px] top-[149px] z-[3] h-[279px] w-[339px] max-[1300px]:hidden">
            <Image
              className="rh2-cta-decoration-image block size-full object-contain"
              src={CtaLeftPerson}
              unoptimized
              alt=""
              width={508}
              height={418}
              sizes="(max-width: 1300px) 0px, 339px"
            />
          </div>
          <div className="rh2-cta-decoration is-right pointer-events-none absolute right-[55px] top-[124px] z-[3] h-[398px] w-[252px] max-[1300px]:hidden">
            <Image
              className="rh2-cta-decoration-image block size-full object-contain"
              src={CtaRightPerson}
              unoptimized
              alt=""
              width={377}
              height={596}
              sizes="(max-width: 1300px) 0px, 252px"
            />
          </div>
          <div className={lightHeadingClass}>
            <p className={kickerClass}>Get started</p>
            <h2>
              A clear way to handle
              <br />
              negative Trustpilot reviews
            </h2>
            <p>
              Choose the reviews you want us to assess. You only pay for reviews Trustpilot removes.
            </p>
          </div>
          <div className="rh2-cta-search !z-10">
            <div className="rh2-start-label">
              <i className="max-[800px]:justify-self-end" />
              Start with a profile search
              <i className="max-[800px]:justify-self-start" />
            </div>
            <div className="rh2-search-card [&>div]:!z-40 max-[520px]:[&>h2]:!text-[26px] max-[520px]:[&>h2]:!leading-[34px]">
              <h2>Find your business</h2>
              <div className="cta-payments my-5 flex flex-wrap items-center justify-center gap-4 text-white">
                <span className="font-jetbrains-mono text-[11px] font-medium uppercase leading-[16.5px]">Card required</span>
                <CardBrandLogos />
              </div>
              <div className="w-full max-[520px]:[&_.rh-inline-search-form]:!h-auto max-[520px]:[&_.rh-inline-search-form>button]:!static max-[520px]:[&_.rh-inline-search-form>button]:!mt-2.5 max-[520px]:[&_.rh-inline-search-form>button]:!w-full max-[520px]:[&_.rh-inline-search-form>button]:!min-w-full max-[520px]:[&_.rh-inline-search-form>button]:!justify-between">
                <ReputechSearchForm submitLabel="Search" />
              </div>
              <ul>
                <li>
                  <HeroBenefitCheck />
                  No upfront charge
                </li>
                <li>
                  <HeroBenefitCheck />
                  Review before you submit
                </li>
                <li>
                  <HeroBenefitCheck />
                  Card required before submission
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section
        id="faq"
        className={`${sectionClass} faq-section rh2-faq !min-h-0 max-[800px]:!px-5 max-[800px]:!py-16`}
      >
        <div className={headingClass}>
          <p className={kickerClass}>FAQ</p>
          <h2>
            Answers to your <span>questions</span>
          </h2>
          <p>
            Learn about the Trustpilot reporting process, pricing, and payment.
          </p>
        </div>
        <Swiper
          className="rh2-faq-tabs mx-auto my-10 h-[51px] w-full max-w-[647px] rounded-full border border-black/[.06] bg-[#edf1f199] !px-2 !py-[7px]"
          slidesPerView="auto"
          spaceBetween={4}
          centerInsufficientSlides
          onSwiper={(swiper) => {
            faqTabsSwiperRef.current = swiper;
          }}
        >
          {displayedFaqCategories.map((category, index) => (
            <SwiperSlide className="!w-auto" key={category.label}>
              <button
                type="button"
                aria-pressed={faqTab === index}
                className={`h-full cursor-pointer whitespace-nowrap rounded-full border-0 px-[18px] !text-sm !font-semibold !leading-[21px] ${faqTab === index ? "active bg-[var(--brand)] !text-white shadow-[0_2px_5px_rgba(0,0,0,.05)]" : "bg-transparent text-[#666572]"}`}
                onClick={() => {
                  setFaqTab(index);
                  setFaqOpen(0);
                  faqTabsSwiperRef.current?.slideTo(index);
                }}
              >
                {category.label}
              </button>
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="rh2-accordion mx-auto grid w-full max-w-[1280px] grid-cols-[repeat(2,minmax(0,628px))] items-start justify-between gap-6 max-[800px]:grid-cols-1">
          {[
            displayedFaqCategories[faqTab].items.slice(
              0,
              Math.ceil(displayedFaqCategories[faqTab].items.length / 2),
            ),
            displayedFaqCategories[faqTab].items.slice(
              Math.ceil(displayedFaqCategories[faqTab].items.length / 2),
            ),
          ].map((items, column) => (
            <div
              className="rh2-faq-column flex flex-col gap-[18px]"
              key={column}
            >
              {items.map(([q, a], index) => {
                const i =
                  index +
                  column * Math.ceil(displayedFaqCategories[faqTab].items.length / 2);
                return (
                  <article
                    className={`rounded-2xl border transition-[background-color,border-color,box-shadow] duration-200 ${faqOpen === i ? "is-open border-[var(--brand)] bg-[var(--brand)] text-white shadow-[0_8px_12px_rgba(60,110,113,.24)]" : "border-black/10 bg-[var(--surface)]"}`}
                    key={q}
                  >
                    <button
                      className={`flex min-h-[80px] w-full cursor-pointer items-center justify-start gap-3 border-0 bg-transparent px-[23px] text-left ${faqOpen === i ? "pb-4 pt-[23px]" : "py-[23px]"} max-[520px]:min-h-16 max-[520px]:p-[15px]`}
                      onClick={() => setFaqOpen(faqOpen === i ? -1 : i)}
                      type="button"
                      aria-controls={`faq-answer-${faqTab}-${i}`}
                      aria-expanded={faqOpen === i}
                    >
                      <Image
                        className="order-1 size-8 flex-none"
                        src={faqOpen === i ? FaqExpandedIcon : FaqCollapsedIcon}
                        alt=""
                        width={32}
                        height={32}
                      />
                      <span className="order-2 text-lg font-medium leading-6 max-[520px]:text-[15px]">
                        {q}
                      </span>
                    </button>
                    {faqOpen === i && (
                      <p
                        id={`faq-answer-${faqTab}-${i}`}
                        className="px-[23px] pb-[23px] text-sm leading-6 text-white/90 max-[520px]:pb-5 max-[520px]:pl-[15px] max-[520px]:pr-[54px]"
                      >
                        {a}
                      </p>
                    )}
                  </article>
                );
              })}
            </div>
          ))}
        </div>
      </section>

      <FooterContact />
    </>
  );
}

export default ReputechLandingPage;
